import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import { useUserSwitcher } from "./UserSwitcherContext";
import { useAuthContext } from "../Auth";
import { useStoreState } from "../../util/util";
import { useLocation } from "react-router-dom";
import useSWR from "swr";
import { endpoints } from "../../endpoints";
import type { PIMProduct } from "../../types/types.PIM";
import { makeUrlWithParams } from "../../util/util";
import { spacingTokens } from "../../designTokens";

const SIZES = {
  fontBase: 12,
  fontSmall: 10,
  fontTiny: 11,
  iconExpand: 11,
  iconCopy: 10,
  checkboxLarge: 15,
  checkboxSmall: 13,
  containerOffset: 13,
  containerWidth: 480,
  headerHeight: 56,
  badgePadding: `${spacingTokens.spacing_xxs} ${spacingTokens.spacing_sm}`,
  buttonPadding: `${spacingTokens.spacing_xs} ${spacingTokens.spacing_md}`,
  gapSmall: 8,
  gapMedium: 10,
} as const;

const MONOSPACE_FONT =
  "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Courier New', monospace";

const PlusIcon: React.FC<{ size?: number; color?: string }> = ({
  size = SIZES.iconExpand,
  color = "currentColor",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block" }}
  >
    <path
      d="M5 1V9M1 5H9"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="square"
    />
  </svg>
);

const MinusIcon: React.FC<{ size?: number; color?: string }> = ({
  size = SIZES.iconExpand,
  color = "currentColor",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block" }}
  >
    <path d="M1 5H9" stroke={color} strokeWidth="1.5" strokeLinecap="square" />
  </svg>
);

const Container = styled.div<{ isExpanded: boolean }>`
  position: fixed;
  bottom: ${SIZES.containerOffset}px;
  right: ${SIZES.containerOffset}px;
  z-index: 900;
  background: ${({ theme }) => theme.primaryBG};
  border: ${({ theme }) => theme.resolve("border_2xl")} solid #0066ff;
  font-family: ${MONOSPACE_FONT};
  font-size: ${SIZES.fontBase}px;
  width: ${({ isExpanded }) =>
    isExpanded ? `${SIZES.containerWidth}px` : "auto"};
  max-height: ${({ isExpanded }) =>
    isExpanded ? `calc(100vh - ${SIZES.containerOffset * 2}px)` : "auto"};
  overflow: hidden;
  transition: none;

  * {
    font-family: ${MONOSPACE_FONT};
  }
`;

const Header = styled.button`
  display: block;
  padding: ${SIZES.gapSmall}px 12px;
  background: #0066ff;
  border: none;
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid #0052cc;
  cursor: pointer;
  width: 100%;
  text-align: left;
  color: #ffffff;
  font-family: ${MONOSPACE_FONT};
  font-size: inherit;
  line-height: 1.2;

  &:hover {
    background: #0052cc;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 1.2;
`;

const HeaderLabel = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: ${SIZES.fontSmall}px;
  color: rgba(255, 255, 255, 0.7);
  font-family: ${MONOSPACE_FONT};
  line-height: 1.2;
  display: block;
`;

const HeaderValue = styled.span`
  font-weight: 500;
  color: #ffffff;
  font-family: ${MONOSPACE_FONT};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 300px;
  display: inline-block;
  line-height: 1.2;
  vertical-align: middle;
`;

const ExpandedContent = styled.div`
  max-height: calc(100vh - ${SIZES.headerHeight}px);
  overflow-y: auto;
  overflow-x: hidden;

  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari/Opera */
  }
`;

const Section = styled.div`
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};

  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${SIZES.gapMedium}px 12px;
  background: ${({ theme }) => theme.secondaryBG};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: ${SIZES.fontSmall}px;
  color: #0066ff;
  border: none;
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  border-left: 3px solid #0066ff;
  cursor: pointer;
  width: 100%;
  text-align: left;
  font-family: ${MONOSPACE_FONT};
  line-height: 1.2;

  &:hover {
    background: ${({ theme }) => theme.primaryBG};
  }
`;

const SectionHeader = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${SIZES.gapMedium}px 12px;
  background: ${({ theme }) => theme.secondaryBG};
  border: none;
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  border-left: 3px solid #0066ff;
  cursor: pointer;
  width: 100%;
  font-family: ${MONOSPACE_FONT};
  line-height: 1.2;

  &:hover {
    background: ${({ theme }) => theme.primaryBG};
  }
`;

const SectionHeaderTitle = styled.div`
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: ${SIZES.fontSmall}px;
  color: #0066ff;
  font-family: ${MONOSPACE_FONT};
  line-height: 1.2;
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const SectionHeaderMeta = styled.div`
  font-size: ${SIZES.fontBase}px;
  color: ${({ theme }) => theme.primaryTextColor};
  text-transform: none;
  letter-spacing: normal;
  font-variant-numeric: tabular-nums;
  font-family: ${MONOSPACE_FONT};
  line-height: 1.2;
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const UserList = styled.div`
  display: block;
`;

const UserItem = styled.button<{ isActive: boolean }>`
  display: grid;
  grid-template-columns: ${SIZES.checkboxLarge}px 1fr;
  gap: ${SIZES.gapMedium}px;
  align-items: center;
  padding: ${({ theme }) => theme.resolve("spacing_sm")}
    ${({ theme }) => theme.resolve("spacing_lg")};
  border: none;
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  background: ${({ theme, isActive }) =>
    isActive ? theme.secondaryBG : theme.primaryBG};
  cursor: pointer;
  text-align: left;
  font-family: ${MONOSPACE_FONT};
  font-size: inherit;
  width: 100%;
  line-height: 1;

  &:hover {
    background: ${({ theme }) => theme.secondaryBG};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const UserCheckbox = styled.div<{ isActive: boolean }>`
  width: ${SIZES.checkboxLarge}px;
  height: ${SIZES.checkboxLarge}px;
  border: ${({ theme }) => theme.resolve("border_2xl")} solid
    ${({ isActive }) => (isActive ? "#0066ff" : "#666666")};
  background: ${({ isActive }) => (isActive ? "#0066ff" : "transparent")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: ${SIZES.fontBase}px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1;
  box-sizing: border-box;

  &::after {
    content: ${({ isActive }) => (isActive ? '"✓"' : '""')};
  }
`;

const UserInfo = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${SIZES.gapMedium}px;
  align-items: center;
  min-width: 0;
`;

const UserLabel = styled.div`
  color: ${({ theme }) => theme.secondaryTextColor};
  white-space: nowrap;
  font-family: ${MONOSPACE_FONT};
  line-height: 1.2;
  display: flex;
  align-items: center;
`;

const UserValue = styled.div`
  color: ${({ theme }) => theme.primaryTextColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${MONOSPACE_FONT};
  line-height: 1.2;
  display: flex;
  align-items: center;
`;

const PermissionList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  max-height: 300px;
  overflow-y: auto;

  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari/Opera */
  }
`;

const PermissionCategory = styled.div`
  grid-column: 1 / -1;
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
`;

const PermissionCategoryHeader = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.resolve("spacing_sm")}
    ${({ theme }) => theme.resolve("spacing_lg")};
  background: ${({ theme }) => theme.secondaryBG};
  border: none;
  cursor: pointer;
  width: 100%;
  font-family: ${MONOSPACE_FONT};
  font-size: ${SIZES.fontSmall}px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.secondaryTextColor};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.primaryBG};
  }
`;

const PermissionCategoryContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

const PermissionItem = styled.button<{ isActive: boolean }>`
  display: grid;
  grid-template-columns: ${SIZES.checkboxSmall}px 1fr;
  gap: ${SIZES.gapMedium}px;
  align-items: center;
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_lg")};
  border: none;
  border-right: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  background: ${({ theme, isActive }) =>
    isActive ? theme.primaryBG : theme.secondaryBG};
  cursor: pointer;
  font-family: ${MONOSPACE_FONT};
  font-size: inherit;
  color: ${({ theme, isActive }) =>
    isActive ? theme.primaryTextColor : theme.secondaryTextColor};
  text-align: left;
  width: 100%;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;

  &:hover {
    background: ${({ theme }) => theme.primaryBG};
  }

  &:nth-child(2n) {
    border-right: none;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.2;
  }
`;

const PermissionCheckbox = styled.div<{ isActive: boolean }>`
  width: ${SIZES.checkboxSmall}px;
  height: ${SIZES.checkboxSmall}px;
  border: ${({ theme }) => theme.resolve("border_2xl")} solid
    ${({ isActive }) => (isActive ? "#0066ff" : "#666666")};
  background: ${({ isActive }) => (isActive ? "#0066ff" : "transparent")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: ${SIZES.fontTiny}px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1;
  box-sizing: border-box;

  &::after {
    content: ${({ isActive }) => (isActive ? '"✓"' : '""')};
  }
`;

const ResetButton = styled.button`
  padding: ${SIZES.buttonPadding};
  background: transparent;
  border: ${({ theme }) => theme.resolve("border_md")} solid #0066ff;
  color: #0066ff;
  font-family: ${MONOSPACE_FONT};
  font-size: ${SIZES.fontSmall}px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #0066ff;
    color: #ffffff;
  }
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${MONOSPACE_FONT};
  font-size: inherit;
`;

const DataRow = styled.tr`
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};

  &:last-child {
    border-bottom: none;
  }
`;

const DataLabel = styled.td`
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_lg")};
  color: #666666;
  white-space: nowrap;
  width: 35%;
  font-weight: 500;
  font-family: ${MONOSPACE_FONT};
  vertical-align: middle;
  line-height: 1.2;
`;

const DataValue = styled.td`
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_lg")}
    ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")};
  color: ${({ theme }) => theme.primaryTextColor};
  font-family: ${MONOSPACE_FONT};
  max-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
  line-height: 1.2;
`;

const StateBadge = styled.span<{ state: "true" | "false" | "warn" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${SIZES.badgePadding};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ state }) =>
      state === "true" ? "#00cc00" : state === "warn" ? "#ff9900" : "#ff3333"};
  background: ${({ state }) =>
    state === "true" ? "#00cc00" : state === "warn" ? "#ff9900" : "#ff3333"};
  color: #000000;
  font-size: ${SIZES.fontSmall}px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  vertical-align: middle;
  line-height: 1;
  font-family: ${MONOSPACE_FONT};
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
  text-align: center;
  color: ${({ theme }) => theme.secondaryTextColor};
  font-family: ${MONOSPACE_FONT};
  line-height: 1.2;
`;

const RoleBadge = styled.span<{ roleType: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${SIZES.badgePadding};
  border: ${({ theme }) => theme.resolve("border_md")} solid;
  font-size: ${SIZES.fontSmall}px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  vertical-align: middle;
  line-height: 1;
  font-family: ${MONOSPACE_FONT};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ roleType }) => {
    switch (roleType) {
      case "agilis_admin":
        return "background: #ff00ff; border-color: #ff00ff; color: #000000;"; // Magenta
      case "seller_admin":
      case "seller_standard":
        return "background: #00ffff; border-color: #00ffff; color: #000000;"; // Cyan
      case "buyer":
        return "background: #ffff00; border-color: #ffff00; color: #000000;"; // Yellow
      case "guest":
        return "background: #ff9900; border-color: #ff9900; color: #000000;"; // Orange
      case "storefront":
        return "background: #0066ff; border-color: #0066ff; color: #ffffff;"; // Blue
      case "user":
        return "background: #00cc00; border-color: #00cc00; color: #000000;"; // Green
      case "product":
        return "background: #000000; border-color: #000000; color: #ffffff;"; // Black terminal
      default:
        return "background: #cc00cc; border-color: #cc00cc; color: #ffffff;"; // Purple
    }
  }}
`;

const ClickableRoleBadge = styled.a<{ roleType: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
  font-size: ${SIZES.fontSmall}px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  vertical-align: middle;
  line-height: 1.2;
  font-family: ${MONOSPACE_FONT};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
  text-decoration-color: #0066ff;
  cursor: pointer;
  color: #0066ff;
  background: transparent;
  border: none;

  &:hover {
    text-decoration-style: solid;
    text-decoration-color: #0052cc;
    color: #0052cc;
  }
`;

const EmailWithCopy = styled.div`
  display: flex;
  align-items: center;
  gap: ${SIZES.gapSmall}px;
  overflow: hidden;
`;

const EmailText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const CopyIcon = styled.button`
  background: none;
  border: ${({ theme }) => theme.resolve("border_md")} solid #0066ff;
  color: #0066ff;
  padding: ${({ theme }) => theme.resolve("spacing_xxs")}
    ${({ theme }) => theme.resolve("spacing_xs")};
  cursor: pointer;
  font-family: ${MONOSPACE_FONT};
  font-size: ${SIZES.fontSmall}px;
  line-height: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #0066ff;
    color: #ffffff;
  }

  &:active {
    background: #00cc00;
    border-color: #00cc00;
    color: #000000;
  }
`;

export const UserSwitcher: React.FC = () => {
  const {
    currentUserId,
    isExpanded,
    toggleExpanded,
    availableCredentials,
    switchUser,
    activePermissions,
    originalPermissions,
    togglePermission,
    resetPermissions,
    permissionsModified,
    isVisible,
  } = useUserSwitcher();

  const { user, role } = useAuthContext();
  const { storefront_metadata, tenant_id } = useStoreState();
  const location = useLocation();

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isExpanded) {
        toggleExpanded();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isExpanded, toggleExpanded]);

  const [sessionCollapsed, setSessionCollapsed] = useState(false);
  const [productCollapsed, setProductCollapsed] = useState(false);
  const [usersCollapsed, setUsersCollapsed] = useState(false);
  const [permissionsCollapsed, setPermissionsCollapsed] = useState(false);
  const [viewPermissionsCollapsed, setViewPermissionsCollapsed] =
    useState(false);
  const [modifyPermissionsCollapsed, setModifyPermissionsCollapsed] =
    useState(false);
  const [deletePermissionsCollapsed, setDeletePermissionsCollapsed] =
    useState(false);
  const [publishPermissionsCollapsed, setPublishPermissionsCollapsed] =
    useState(false);
  const [otherPermissionsCollapsed, setOtherPermissionsCollapsed] =
    useState(false);

  const productIdMatch = location.pathname.match(
    /\/admin\/pim\/products\/([^/?]+)/
  );

  // Ignore "new" as product id, as it is creation route
  const product_id =
    productIdMatch && productIdMatch[1] !== "new" ? productIdMatch[1] : null;

  const productUrl =
    product_id && tenant_id
      ? makeUrlWithParams(
          endpoints.v2_tenants_id_pim_products_id(tenant_id, product_id),
          { use_database: "true", return_staged: "true" }
        )
      : null;

  const { data: productData } = useSWR<PIMProduct>(productUrl);

  if (!isVisible) {
    return null;
  }

  const displayUser = user;
  const displayRole = user?.rbac_role || role;
  const email = user?.email_address || "Not logged in";

  const allPermissions =
    originalPermissions.length > 0 ? originalPermissions : activePermissions;

  const viewPermissions = allPermissions.filter((p) => p.startsWith("view_"));
  const modifyPermissions = allPermissions.filter((p) =>
    p.startsWith("modify_")
  );
  const deletePermissions = allPermissions.filter((p) =>
    p.startsWith("delete_")
  );
  const publishPermissions = allPermissions.filter((p) =>
    p.startsWith("publish_")
  );
  const otherPermissions = allPermissions.filter(
    (p) =>
      !p.startsWith("view_") &&
      !p.startsWith("modify_") &&
      !p.startsWith("delete_") &&
      !p.startsWith("publish_")
  );

  const groupedPermissions = [
    ...viewPermissions,
    ...modifyPermissions,
    ...deletePermissions,
    ...publishPermissions,
    ...otherPermissions,
  ];

  return (
    <Container isExpanded={isExpanded}>
      <Header onClick={toggleExpanded}>
        <HeaderContent>
          <div>
            <HeaderLabel>DEV TOOLS</HeaderLabel>
            {!isExpanded && (
              <div style={{ marginTop: spacingTokens.spacing_xxs }}>
                <HeaderValue>{email}</HeaderValue>
              </div>
            )}
          </div>
          <span
            style={{ color: "#ffffff", display: "flex", alignItems: "center" }}
          >
            {isExpanded ? (
              <MinusIcon color="#ffffff" />
            ) : (
              <PlusIcon color="#ffffff" />
            )}
          </span>
        </HeaderContent>
      </Header>

      {isExpanded && (
        <ExpandedContent>
          <Section>
            <SectionTitle
              onClick={() => setSessionCollapsed(!sessionCollapsed)}
            >
              <span>Session</span>
              <span style={{ display: "flex", alignItems: "center" }}>
                {sessionCollapsed ? (
                  <PlusIcon color="#0066ff" />
                ) : (
                  <MinusIcon color="#0066ff" />
                )}
              </span>
            </SectionTitle>
            {!sessionCollapsed && (
              <DataTable>
                <tbody>
                  <DataRow>
                    <DataLabel>storefront:</DataLabel>
                    <DataValue>
                      <RoleBadge roleType="storefront">
                        {storefront_metadata?.slug || "—"}
                      </RoleBadge>
                    </DataValue>
                  </DataRow>
                  <DataRow>
                    <DataLabel>email:</DataLabel>
                    <DataValue>
                      <EmailWithCopy>
                        <EmailText>{email}</EmailText>
                        <CopyIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(email);
                          }}
                          title="Copy email to clipboard"
                        >
                          ⎘
                        </CopyIcon>
                      </EmailWithCopy>
                    </DataValue>
                  </DataRow>
                  <DataRow>
                    <DataLabel>name:</DataLabel>
                    <DataValue>
                      {displayUser ? (
                        <ClickableRoleBadge
                          roleType="user"
                          href={`/store/${
                            storefront_metadata?.slug || ""
                          }/admin/organization/users?q=${encodeURIComponent(
                            `${displayUser.firstname} ${displayUser.lastname}`
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {`${displayUser.firstname} ${displayUser.lastname}`}
                        </ClickableRoleBadge>
                      ) : (
                        <RoleBadge roleType="user">—</RoleBadge>
                      )}
                    </DataValue>
                  </DataRow>
                  <DataRow>
                    <DataLabel>role:</DataLabel>
                    <DataValue>
                      <RoleBadge roleType={displayRole || ""}>
                        {displayRole?.replace(/_/g, " ") || "—"}
                      </RoleBadge>
                    </DataValue>
                  </DataRow>
                </tbody>
              </DataTable>
            )}
          </Section>

          {productData && (
            <Section>
              <SectionTitle
                onClick={() => setProductCollapsed(!productCollapsed)}
              >
                <span>Product Access</span>
                <span style={{ display: "flex", alignItems: "center" }}>
                  {productCollapsed ? (
                    <PlusIcon color="#0066ff" />
                  ) : (
                    <MinusIcon color="#0066ff" />
                  )}
                </span>
              </SectionTitle>
              {!productCollapsed && (
                <DataTable>
                  <tbody>
                    <DataRow>
                      <DataLabel>product:</DataLabel>
                      <DataValue>
                        <RoleBadge roleType="product">
                          {productData.product_number || productData.id}
                        </RoleBadge>
                      </DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>is_editable:</DataLabel>
                      <DataValue>
                        <StateBadge
                          state={productData.is_editable ? "true" : "false"}
                        >
                          {productData.is_editable ? "TRUE" : "FALSE"}
                        </StateBadge>
                      </DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>is_locked:</DataLabel>
                      <DataValue>
                        <StateBadge
                          state={productData.is_locked ? "warn" : "false"}
                        >
                          {productData.is_locked ? "TRUE" : "FALSE"}
                        </StateBadge>
                      </DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>is_viewable:</DataLabel>
                      <DataValue>
                        <StateBadge
                          state={productData.is_viewable ? "true" : "false"}
                        >
                          {productData.is_viewable ? "TRUE" : "FALSE"}
                        </StateBadge>
                      </DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>is_publishable:</DataLabel>
                      <DataValue>
                        <StateBadge
                          state={productData.is_publishable ? "true" : "false"}
                        >
                          {productData.is_publishable ? "TRUE" : "FALSE"}
                        </StateBadge>
                      </DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>permission:</DataLabel>
                      <DataValue>
                        {productData.non_team_permission?.toUpperCase() || "—"}
                      </DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>list_price_vis:</DataLabel>
                      <DataValue>
                        {productData.list_price_visibility?.toUpperCase() ||
                          "—"}
                      </DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>teams:</DataLabel>
                      <DataValue>
                        {productData.team_names &&
                        productData.team_names.length > 0
                          ? productData.team_names.join(", ")
                          : "—"}
                      </DataValue>
                    </DataRow>
                  </tbody>
                </DataTable>
              )}
            </Section>
          )}

          <Section>
            <SectionTitle onClick={() => setUsersCollapsed(!usersCollapsed)}>
              <span>Switch User</span>
              <span style={{ display: "flex", alignItems: "center" }}>
                {usersCollapsed ? (
                  <PlusIcon color="#0066ff" />
                ) : (
                  <MinusIcon color="#0066ff" />
                )}
              </span>
            </SectionTitle>
            {!usersCollapsed && (
              <>
                {availableCredentials.length === 0 ? (
                  <EmptyState>no users configured</EmptyState>
                ) : (
                  <UserList>
                    {availableCredentials.map((cred) => (
                      <UserItem
                        key={cred.id}
                        isActive={currentUserId === cred.id}
                        onClick={() => switchUser(cred.id)}
                      >
                        <UserCheckbox isActive={currentUserId === cred.id} />
                        <UserInfo>
                          <UserLabel>user:</UserLabel>
                          <UserValue>
                            {cred.display_name || cred.email}
                          </UserValue>
                        </UserInfo>
                      </UserItem>
                    ))}
                  </UserList>
                )}
              </>
            )}
          </Section>

          {groupedPermissions.length > 0 && (
            <Section>
              <SectionHeader
                onClick={() => setPermissionsCollapsed(!permissionsCollapsed)}
              >
                <div
                  style={{
                    display: "flex",
                    gap: spacingTokens.spacing_md,
                    alignItems: "center",
                  }}
                >
                  <SectionHeaderTitle>Permissions</SectionHeaderTitle>
                  <SectionHeaderMeta>
                    {activePermissions.length}/{originalPermissions.length}{" "}
                    active
                    {permissionsModified && " (modified)"}
                  </SectionHeaderMeta>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacingTokens.spacing_md,
                  }}
                >
                  {permissionsModified && !permissionsCollapsed && (
                    <ResetButton
                      onClick={(e) => {
                        e.stopPropagation();
                        resetPermissions();
                      }}
                    >
                      Reset
                    </ResetButton>
                  )}
                  <span
                    style={{
                      color: "#0066ff",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {permissionsCollapsed ? (
                      <PlusIcon color="#0066ff" />
                    ) : (
                      <MinusIcon color="#0066ff" />
                    )}
                  </span>
                </div>
              </SectionHeader>
              {!permissionsCollapsed && (
                <PermissionList>
                  {viewPermissions.length > 0 && (
                    <PermissionCategory>
                      <PermissionCategoryHeader
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewPermissionsCollapsed(
                            !viewPermissionsCollapsed
                          );
                        }}
                      >
                        <span>
                          View (
                          {
                            viewPermissions.filter((p) =>
                              activePermissions.includes(p)
                            ).length
                          }
                          /{viewPermissions.length})
                        </span>
                        <span style={{ display: "flex", alignItems: "center" }}>
                          {viewPermissionsCollapsed ? (
                            <PlusIcon color="#666666" size={10} />
                          ) : (
                            <MinusIcon color="#666666" size={10} />
                          )}
                        </span>
                      </PermissionCategoryHeader>
                      {!viewPermissionsCollapsed && (
                        <PermissionCategoryContent>
                          {viewPermissions.map((permission) => (
                            <PermissionItem
                              key={permission}
                              isActive={activePermissions.includes(permission)}
                              onClick={() => togglePermission(permission)}
                              title={permission}
                            >
                              <PermissionCheckbox
                                isActive={activePermissions.includes(
                                  permission
                                )}
                              />
                              <span>{permission}</span>
                            </PermissionItem>
                          ))}
                        </PermissionCategoryContent>
                      )}
                    </PermissionCategory>
                  )}

                  {modifyPermissions.length > 0 && (
                    <PermissionCategory>
                      <PermissionCategoryHeader
                        onClick={(e) => {
                          e.stopPropagation();
                          setModifyPermissionsCollapsed(
                            !modifyPermissionsCollapsed
                          );
                        }}
                      >
                        <span>
                          Modify (
                          {
                            modifyPermissions.filter((p) =>
                              activePermissions.includes(p)
                            ).length
                          }
                          /{modifyPermissions.length})
                        </span>
                        <span style={{ display: "flex", alignItems: "center" }}>
                          {modifyPermissionsCollapsed ? (
                            <PlusIcon color="#666666" size={10} />
                          ) : (
                            <MinusIcon color="#666666" size={10} />
                          )}
                        </span>
                      </PermissionCategoryHeader>
                      {!modifyPermissionsCollapsed && (
                        <PermissionCategoryContent>
                          {modifyPermissions.map((permission) => (
                            <PermissionItem
                              key={permission}
                              isActive={activePermissions.includes(permission)}
                              onClick={() => togglePermission(permission)}
                              title={permission}
                            >
                              <PermissionCheckbox
                                isActive={activePermissions.includes(
                                  permission
                                )}
                              />
                              <span>{permission}</span>
                            </PermissionItem>
                          ))}
                        </PermissionCategoryContent>
                      )}
                    </PermissionCategory>
                  )}

                  {deletePermissions.length > 0 && (
                    <PermissionCategory>
                      <PermissionCategoryHeader
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletePermissionsCollapsed(
                            !deletePermissionsCollapsed
                          );
                        }}
                      >
                        <span>
                          Delete (
                          {
                            deletePermissions.filter((p) =>
                              activePermissions.includes(p)
                            ).length
                          }
                          /{deletePermissions.length})
                        </span>
                        <span style={{ display: "flex", alignItems: "center" }}>
                          {deletePermissionsCollapsed ? (
                            <PlusIcon color="#666666" size={10} />
                          ) : (
                            <MinusIcon color="#666666" size={10} />
                          )}
                        </span>
                      </PermissionCategoryHeader>
                      {!deletePermissionsCollapsed && (
                        <PermissionCategoryContent>
                          {deletePermissions.map((permission) => (
                            <PermissionItem
                              key={permission}
                              isActive={activePermissions.includes(permission)}
                              onClick={() => togglePermission(permission)}
                              title={permission}
                            >
                              <PermissionCheckbox
                                isActive={activePermissions.includes(
                                  permission
                                )}
                              />
                              <span>{permission}</span>
                            </PermissionItem>
                          ))}
                        </PermissionCategoryContent>
                      )}
                    </PermissionCategory>
                  )}

                  {publishPermissions.length > 0 && (
                    <PermissionCategory>
                      <PermissionCategoryHeader
                        onClick={(e) => {
                          e.stopPropagation();
                          setPublishPermissionsCollapsed(
                            !publishPermissionsCollapsed
                          );
                        }}
                      >
                        <span>
                          Publish (
                          {
                            publishPermissions.filter((p) =>
                              activePermissions.includes(p)
                            ).length
                          }
                          /{publishPermissions.length})
                        </span>
                        <span style={{ display: "flex", alignItems: "center" }}>
                          {publishPermissionsCollapsed ? (
                            <PlusIcon color="#666666" size={10} />
                          ) : (
                            <MinusIcon color="#666666" size={10} />
                          )}
                        </span>
                      </PermissionCategoryHeader>
                      {!publishPermissionsCollapsed && (
                        <PermissionCategoryContent>
                          {publishPermissions.map((permission) => (
                            <PermissionItem
                              key={permission}
                              isActive={activePermissions.includes(permission)}
                              onClick={() => togglePermission(permission)}
                              title={permission}
                            >
                              <PermissionCheckbox
                                isActive={activePermissions.includes(
                                  permission
                                )}
                              />
                              <span>{permission}</span>
                            </PermissionItem>
                          ))}
                        </PermissionCategoryContent>
                      )}
                    </PermissionCategory>
                  )}

                  {otherPermissions.length > 0 && (
                    <PermissionCategory>
                      <PermissionCategoryHeader
                        onClick={(e) => {
                          e.stopPropagation();
                          setOtherPermissionsCollapsed(
                            !otherPermissionsCollapsed
                          );
                        }}
                      >
                        <span>
                          Other (
                          {
                            otherPermissions.filter((p) =>
                              activePermissions.includes(p)
                            ).length
                          }
                          /{otherPermissions.length})
                        </span>
                        <span style={{ display: "flex", alignItems: "center" }}>
                          {otherPermissionsCollapsed ? (
                            <PlusIcon color="#666666" size={10} />
                          ) : (
                            <MinusIcon color="#666666" size={10} />
                          )}
                        </span>
                      </PermissionCategoryHeader>
                      {!otherPermissionsCollapsed && (
                        <PermissionCategoryContent>
                          {otherPermissions.map((permission) => (
                            <PermissionItem
                              key={permission}
                              isActive={activePermissions.includes(permission)}
                              onClick={() => togglePermission(permission)}
                              title={permission}
                            >
                              <PermissionCheckbox
                                isActive={activePermissions.includes(
                                  permission
                                )}
                              />
                              <span>{permission}</span>
                            </PermissionItem>
                          ))}
                        </PermissionCategoryContent>
                      )}
                    </PermissionCategory>
                  )}
                </PermissionList>
              )}
            </Section>
          )}
        </ExpandedContent>
      )}
    </Container>
  );
};
